<template>
  <ul v-show="showSuggestions" ref="suggestions" class="list-group">
    <template v-if="hasResults">
      <li
        v-for="(item, index) in filteredItems"
        :key="index"
        class="list-group-item"
        :class="{ active: navigatedIndex === index }"
        @click="selectItem(item)"
        @mouseover="navigatedIndex = index"
      >
        <slot :item="item">{{ item.label }}</slot>
      </li>
    </template>
  </ul>
</template>

<script>
  // https://github.com/scrumpy/tiptap/blob/master/examples/Components/Routes/Suggestions/index.vue
  import { Mention } from 'tiptap-extensions';
  import { replaceText } from 'tiptap-commands';
  import SuggestionsPlugin from 'tiptap-extensions/src/plugins/Suggestions';
  import Fuse from 'fuse.js';
  import tippy from 'tippy.js';
  import debounce from 'lodash.debounce';

  // https://github.com/scrumpy/tiptap/blob/master/packages/tiptap-extensions/src/nodes/Mention.js
  class CustomMention extends Mention {
    get name() {
      return this.options.name;
    }

    get icon() {
      if (this.options.iconClass) {
        const icon = ['i', { class: 'fa ' + this.options.iconClass }];
        return [' ', icon];
      }

      return [];
    }
    // add space after mention
    get space() {
      if (this.options.spaceAfter) {
        return [' '];
      }

      return [];
    }

    get schema() {
      return {
        attrs: {
          id: {},
          label: {},
        },
        group: 'inline',
        inline: true,
        selectable: false,
        atom: true,
        toDOM: (node) => [
          'span',
          {
            class: this.options.mentionClass,
            'data-mention-id': node.attrs.id,
          },
          `${this.options.matcher.char}${node.attrs.label}`,
          ...this.icon,
          ...this.space,
        ],
        parseDOM: [
          {
            tag: `span[data-mention-type="${this.options.mentionType}"][data-mention-id]`,
            getAttrs: (dom) => {
              const id = dom.getAttribute('data-mention-id');
              const label = dom.innerText.trim().split(this.options.matcher.char).join('');
              return { id, label };
            },
          },
        ],
      };
    }

    get plugins() {
      return [
        SuggestionsPlugin({
          command: ({ range, attrs, schema }) => replaceText(range, schema.nodes[this.name], attrs),
          appendText: '',
          matcher: this.options.matcher,
          items: this.options.items,
          onEnter: this.options.onEnter,
          onChange: this.options.onChange,
          onExit: this.options.onExit,
          onKeyDown: this.options.onKeyDown,
          onFilter: this.options.onFilter,
          suggestionClass: this.options.suggestionClass,
        }),
      ];
    }
  }

  export default {
    name: 'RichSuggestion',

    props: {
      pluginName: String,
      editor: null,
      mentionClass: String,
      insertNew: Boolean,
      lookupFunction: Function,
      matcherChar: String,
      iconClass: String,
      mentionType: String,
      spaceAfter: Boolean,
    },

    data() {
      return {
        keywords: '',
        suggestionRange: null,
        filteredItems: [],
        navigatedIndex: 0,
        insertMention: () => {},
        observer: null,
        plugin: null,
      };
    },

    computed: {
      query: {
        get() {
          return this.keywords;
        },

        set(value) {
          this.keywords = value ? value.replace(/[^a-z0-9-_]+/gi, '') : '';
        },
      },

      hasResults() {
        return this.filteredItems.length;
      },

      showSuggestions() {
        return this.hasResults;
      },
    },

    mounted() {
      this.createPlugin();
    },

    beforeDestroy() {
      this.destroyPopup();
    },

    methods: {
      createPlugin() {
        this.plugin = new CustomMention({
          name: this.pluginName,
          matcher: { char: this.matcherChar },
          iconClass: this.iconClass,
          mentionClass: this.mentionClass,
          mentionType: this.mentionType,
          spaceAfter: this.spaceAfter,

          // a list of all suggested items
          items: () => this.filteredItems,
          onEnter: this.onEnter,
          onChange: this.onChange,
          onExit: this.onExit,
          onKeyDown: this.onKeyDown,
          onFilter: this.onFilter,
        });
      },

      // update suggestion and render popup
      showResults({ items, query, range, virtualNode }) {
        this.query = query;
        this.filteredItems = items;
        this.suggestionRange = range;
        this.navigatedIndex = 0;
        if (items.length && virtualNode) this.renderPopup(virtualNode);
      },

      // run lookup
      startLookup: debounce(function ({ query, range, virtualNode }) {
        this.lookupFunction(query)
          .then((items) => items || Promise.reject())
          .then((items) =>
            this.suggestionRange && this.suggestionRange.from === range.from ? items : Promise.reject()
          )
          .then((items) => this.showResults({ items, query, range, virtualNode }))
          .catch(() => {});
      }, 300),

      // is called when a suggestion starts
      onEnter({ items, query, range, command, virtualNode }) {
        // we save the command for inserting a selected mention
        // this allows us to call it inside of our custom popup
        // via keyboard navigation and on click
        this.insertMention = command;
        this.showResults({ items, query, range, virtualNode });
        this.startLookup({ query, range, virtualNode });
      },

      // is called when a suggestion is cancelled
      onChange({ items, query, range, virtualNode }) {
        this.showResults({ items, query, range, virtualNode });
        this.startLookup({ query, range, virtualNode });
      },

      // is called when a suggestion is cancelled
      onExit() {
        // reset all saved values
        this.query = null;
        this.filteredItems = [];
        this.suggestionRange = null;
        this.navigatedIndex = 0;
        this.destroyPopup();
      },

      // is called on every keyDown event while a suggestion is active
      onKeyDown({ event }) {
        // pressing up arrow
        if (event.keyCode === 38) {
          this.upHandler();
          return true;
        }

        // pressing down arrow
        if (event.keyCode === 40) {
          this.downHandler();
          return true;
        }

        // pressing enter
        if (event.keyCode === 13) {
          return this.enterHandler();
        }

        // pressing space 32, tab 9, comma 188, period 190, semi-colon 186
        if ([32, 9, 188, 190, 186].includes(event.keyCode)) {
          this.spaceHandler();
          return false;
        }

        // pressing escape
        if (event.keyCode === 27) {
          this.destroyPopup();
          return true;
        }

        return false;
      },

      // is called when a suggestion has changed
      // this function is optional because there is basic filtering built-in
      // you can overwrite it if you prefer your own filtering
      // in this example we use fuse.js with support for fuzzy search
      onFilter(items, query) {
        if (!query) {
          return items;
        }

        const fuse = new Fuse(items, {
          threshold: 0.2,
          keys: ['label'],
        });

        return fuse.search(query);
      },

      // navigate to the previous item
      // if it's the first item, navigate to the last one
      upHandler() {
        this.navigatedIndex = (this.navigatedIndex + this.filteredItems.length - 1) % this.filteredItems.length;
      },

      // navigate to the next item
      // if it's the last item, navigate to the first one
      downHandler() {
        this.navigatedIndex = (this.navigatedIndex + 1) % this.filteredItems.length;
      },

      // selected item
      getSelectedItem() {
        if (this.filteredItems.length > 0) {
          return this.filteredItems[this.navigatedIndex];
        }
      },

      // new item
      getNewItem() {
        if (this.insertNew && this.query && this.query.match(/^[a-z0-9-_]+$/i)) {
          return { id: this.query, label: this.query };
        }
      },

      // matching item
      getMatchingItem() {
        return this.filteredItems.filter(({ label }) => label.toLowerCase() === this.query.toLowerCase()).pop();
      },

      // select item on the suggestion
      enterHandler() {
        let item;

        if ((item = this.getSelectedItem())) {
          this.selectItem(item);
          return true;
        }

        if ((item = this.getNewItem())) {
          this.selectItem(item);
        }

        return false;
      },

      // space, find the exact match
      spaceHandler() {
        const item = this.getMatchingItem() || this.getNewItem();

        if (item) {
          this.selectItem(item);
          return true;
        }

        return false;
      },

      // we have to replace our suggestion text with a mention
      // so it's important to pass also the position of your suggestion text
      selectItem(item) {
        this.insertMention({
          range: this.suggestionRange,
          attrs: item,
        });

        if (this.editor) this.editor.focus();
      },

      // renders a popup with suggestions
      // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
      renderPopup(node) {
        const rect = node.getBoundingClientRect();
        const { x, y } = rect;
        if (x === 0 && y === 0) {
          return;
        }
        node.cachedRect = rect;

        if (this.popup) {
          return;
        }

        this.popup = tippy('body', {
          getReferenceClientRect: () => node.cachedRect,
          appendTo: () => document.body,
          content: this.$refs.suggestions,
          trigger: 'mouseenter',
          interactive: true,
          placement: 'bottom-start',
          showOnCreate: true,
          arrow: false,
          theme: 'cmrad',
        });

        // we have to update tippy whenever the DOM is updated
        if (MutationObserver) {
          this.observer = new MutationObserver(() => {
            this.popup.popperInstance?.scheduleUpdate();
          });

          this.observer.observe(this.$refs.suggestions, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      },

      destroyPopup() {
        if (this.popup) {
          this.popup = null;
        }

        if (this.observer) {
          this.observer.disconnect();
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '../../sass/config/_variables.scss';

  .tippy-tooltip.cmrad-theme {
    padding: 0;
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    text-align: inherit;
    border-radius: 0;
    cursor: pointer;

    .tippy-backdrop {
      background-color: transparent;
    }

    .list-group-item {
      &.active {
        background-color: var(--gray-200);
        color: var(--gray-800);
      }
    }
  }

  .mention-suggestion {
    font-weight: 400;
    font-size: 14.5px;
    border-radius: $border-radius;
    color: var(--white);
    padding: 0.3rem 0.5rem;
    border: $input-border;
    background-color: var(--gray-900);
  }
</style>
